/**
 * Account and business-related types.
 */

import { BaseEntity } from './base';

// ============================================================================
// ACCOUNT
// ============================================================================

/**
 * Stripe subscription statuses
 * - active: Subscription is active (paying or in trial)
 * - trialing: Subscription is in trial period
 * - past_due: Payment failed but subscription not yet canceled
 * - canceled: Subscription has been canceled
 * - unpaid: Payment failed and subscription is paused
 * - incomplete: Initial payment failed
 * - incomplete_expired: Initial payment failed and subscription expired
 * - paused: Subscription is paused
 */
export type StripeSubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

export interface Account extends BaseEntity {
  adminId: string;
  name: string;
  stripeCustomerId?: string;
  stripeAccountId?: string;
  /** Mirrored from Stripe Connect account.updated webhook */
  charges_enabled?: boolean;
  /** Mirrored from Stripe Connect account.updated webhook */
  payouts_enabled?: boolean;
  stripeSubscriptionId?: string;
  stripeStatus?: StripeSubscriptionStatus;
  /** Unix timestamp (seconds) when the trial ends */
  stripeTrialEnd?: number;
  twilioPhoneNumber?: string;
  settings?: AccountSettings;
  reportSettings?: ReportSettings;
  /** Branding settings for the check-in kiosk app */
  checkInBranding?: CheckInBranding;
  status: AccountStatus;
  configuration?: Record<string, unknown>;
  nylasGrantId?: string;
  nylasCalendarId?: string;
  nylasClientId?: boolean;
  isOnboardingComplete?: boolean;

  /**
   * Case identifier sequence counters, keyed by year and prefix.
   * Example: { "2026": { "SV": 15, "EX": 8 } }
   * Used to generate case IDs like SV-2026-016
   */
  caseSequences?: Record<string, Record<string, number>>;
}

export type AccountStatus = 'active' | 'inactive' | 'suspended' | 'pending';

export interface AccountSettings {
  timezone: string;
  currency: string;
  notifications: AccountNotificationSettings;
  billing: AccountBillingSettings;
  features: AccountFeatureSettings;
}

export interface AccountNotificationSettings {
  email: boolean;
  sms: boolean;
  push: boolean;
  visitReminders: boolean;
  reportNotifications: boolean;
  paymentNotifications: boolean;
  systemNotifications: boolean;
}

export interface AccountBillingSettings {
  autoCharge: boolean;
  paymentMethod?: string;
  billingCycle: 'monthly' | 'quarterly' | 'yearly';
  invoiceDueDays: number;
  lateFeePercentage?: number;
}

export interface AccountFeatureSettings {
  allowPhotos: boolean;
  allowVideos: boolean;
  requireCheckIn: boolean;
  requireCheckOut: boolean;
  autoGenerateReports: boolean;
  enableSMS: boolean;
  enableEmailNotifications: boolean;
  enablePushNotifications: boolean;
  /** Whether to capture a photo of each person during kiosk check-in */
  requirePhotoOnCheckIn?: boolean;
  /** Collect visit payments online through the agency's Stripe Connect account */
  enableStripePayments?: boolean;
}

// ============================================================================
// CHECK-IN KIOSK BRANDING
// ============================================================================

export interface CheckInBranding {
  /** Welcome message shown on the kiosk check-in screen */
  welcomeMessage?: string;
  /** Primary color hex for kiosk buttons and accents (e.g. "#1E3C5A") */
  primaryColor?: string;
  /** URL to a custom logo for the kiosk (falls back to business logoUrl) */
  kioskLogoUrl?: string;
}

// ============================================================================
// REPORT SETTINGS
// ============================================================================

export interface ReportSettings {
  /** Whether to show GPS coordinates (latitude/longitude) in PDF reports */
  showCoordinatesInReport: boolean;
  /** Whether to include event images in the PDF */
  includeImagesInReport: boolean;
  /** Whether to show location map in PDF reports */
  showLocationMapInReport?: boolean;
  /** Default timezone for reports (overrides location timezone if set) */
  reportTimezone?: string;
  /** Header text to display on reports */
  reportHeaderText?: string;
  /** Footer text to display on reports */
  reportFooterText?: string;
  /** Whether to show only staff initials (e.g., "L.S.") instead of full names in reports and visit notes */
  showStaffInitialsOnly?: boolean;
}

// ============================================================================
// BUSINESS
// ============================================================================

/**
 * Type of organization operating the business.
 * Used for reporting, compliance, and partnership eligibility.
 */
export type OrganizationType =
  | 'for_profit'
  | 'nonprofit'
  | 'government'
  | 'educational'
  | 'religious'
  | 'individual'
  | 'other';

/**
 * IRS nonprofit designation status.
 * Only applicable when organizationType is 'nonprofit'.
 */
export type NonprofitStatus = '501c3' | '501c4' | 'other' | 'pending';

/**
 * Verification status for nonprofit claims.
 * - unverified: No verification attempted
 * - self_reported: User claims nonprofit status but not verified
 * - verified: Verified through official channels (EIN lookup, etc.)
 */
export type NonprofitVerificationStatus = 'unverified' | 'self_reported' | 'verified';

/**
 * Range of years the organization has been in operation.
 * Used for grant eligibility and partnership assessments.
 */
export type YearsInOperationRange =
  | 'less_than_1'
  | '1_to_3'
  | '3_to_5'
  | '5_to_10'
  | '10_plus';

/**
 * Range of average monthly cases handled.
 * Used for capacity planning and tier classification.
 */
export type MonthlyCasesRange =
  | '1_to_10'
  | '11_to_25'
  | '26_to_50'
  | '51_to_100'
  | '100_plus';

/**
 * Types of services the organization provides.
 * Multiple can be selected.
 */
export type ServiceFocus =
  | 'supervised_visitation'
  | 'therapeutic_visitation'
  | 'exchanges_only'
  | 'reunification_therapy'
  | 'monitoring_only'
  | 'parent_education'
  | 'other';

/**
 * Nonprofit-specific details.
 * Only populated when organizationType is 'nonprofit'.
 */
export interface NonprofitDetails {
  /** IRS nonprofit status designation */
  status: NonprofitStatus;
  /** Employer Identification Number (EIN) - format: XX-XXXXXXX */
  ein?: string;
  /** State where the nonprofit is registered */
  stateOfRegistration?: string;
  /** Verification status of nonprofit claims */
  verificationStatus: NonprofitVerificationStatus;
  /** Date nonprofit status was verified (if applicable) */
  verifiedAt?: Date;
}

/**
 * Organizational profile information.
 * Captures business context for reporting, grants, and compliance.
 */
export interface OrganizationProfile {
  /** Official legal name of the organization (may differ from DBA name) */
  legalName?: string;
  /** Type of organization */
  organizationType?: OrganizationType;
  /** Nonprofit-specific details (only when organizationType is 'nonprofit') */
  nonprofitDetails?: NonprofitDetails;
  /** How long the organization has been operating */
  yearsInOperation?: YearsInOperationRange;
  /** Average number of cases handled per month */
  averageMonthlyCases?: MonthlyCasesRange;
  /** Types of services provided (multi-select) */
  serviceFocus?: ServiceFocus[];
  /** Whether the organization receives government funding */
  receivesGovernmentFunding?: boolean;
  /** Names of affiliated organizations or networks */
  affiliatedOrganizations?: string[];
}

export interface Business extends BaseEntity {
  /** URL to the business logo image in Firebase Storage */
  logoUrl?: string;
  /** Display name / DBA name of the business */
  name?: string;
  /** Primary phone number */
  phoneNumber?: string;
  /** Fax number (if applicable) */
  faxNumber?: string;
  /** Primary contact email */
  email?: string;
  /** Business website URL */
  website?: string;
  /** Description of the business */
  about?: string;
  /** Foreign key linking to the Account */
  accountId: string;
  /** Street address line 1 */
  streetAddress?: string;
  /** Street address line 2 (suite, unit, etc.) */
  streetAddress2?: string;
  /** City */
  city?: string;
  /** State (2-letter code) */
  state?: string;
  /** ZIP code */
  zipCode?: string;

  // ===== NEW ORGANIZATION PROFILE FIELDS =====

  /** Extended organizational profile information */
  organizationProfile?: OrganizationProfile;
}

/**
 * Helper function to check if a business is a nonprofit.
 * Returns true if organizationType is 'nonprofit'.
 */
export function isNonprofit(business: Business): boolean {
  return business.organizationProfile?.organizationType === 'nonprofit';
}

/**
 * Default organization profile for new businesses.
 * All fields are optional to maintain backward compatibility.
 */
export const DEFAULT_ORGANIZATION_PROFILE: OrganizationProfile = {
  organizationType: undefined,
  legalName: undefined,
  nonprofitDetails: undefined,
  yearsInOperation: undefined,
  averageMonthlyCases: undefined,
  serviceFocus: undefined,
  receivesGovernmentFunding: undefined,
  affiliatedOrganizations: undefined,
};

// ============================================================================
// LOCATION
// ============================================================================

export interface Location extends BaseEntity {
  name: string;
  formattedAddress: string;
  caseId: string;
  latitude: string;
  longitude: string;
  placeId: string;
  accountId: string;
  timeZoneId?: string;
  isFavorite?: boolean;
  caseIds: string[];
}

export type LocationType = 'home' | 'court' | 'supervision-center' | 'office' | 'other';
export type LocationStatus = 'active' | 'inactive' | 'temporary';

// ============================================================================
// SETTINGS
// ============================================================================

export interface Setting extends BaseEntity {
  key: string;
  value: unknown;
  type: SettingType;
  description?: string;
  isPublic: boolean;
  category: SettingCategory;
}

export type SettingType = 'string' | 'number' | 'boolean' | 'object' | 'array';
export type SettingCategory = 'account' | 'business' | 'supervisor' | 'system' | 'notification' | 'billing';
